<template>
	<div class="chat-messages-page">
		<div class="chat-messages-content">
			<!-- Loading spinner -->
			<div v-if="isLoading" class="loading-overlay">
				<div class="spinner"></div>
				<div class="loading-text">Executing query...</div>
			</div>

			<!-- Filter section -->
			<div class="card filter-card">
				<h5 class="card-title">Filters</h5>
				<form @submit.prevent="applyFilters">
					<div class="filter-row">
						<div class="filter-group">
							<label for="customer-id">Customer ID</label>
							<div class="input-with-clear">
								<input
									id="customer-id"
									v-model="filters.customerId"
									type="text"
									placeholder="Enter Customer ID"
									class="form-input"
								/>
								<span v-if="filters.customerId" class="clear-icon" @click="clearCustomerId">&times;</span>
							</div>
						</div>
						<div class="filter-group">
							<label for="from-date">From Date</label>
							<input
								id="from-date"
								v-model="filters.fromDate"
								type="date"
								class="form-input"
							/>
							<input
								id="from-time"
								v-model="filters.fromTime"
								type="time"
								class="form-input"
							/>
						</div>
						<div class="filter-group">
							<label for="to-date">To Date</label>
							<input
								id="to-date"
								v-model="filters.toDate"
								type="date"
								class="form-input"
							/>
							<input
								id="to-time"
								v-model="filters.toTime"
								type="time"
								class="form-input"
							/>
						</div>
						<div class="filter-group filter-button-group">
							<button type="submit" class="btn btn-primary">Apply Filters</button>
						</div>
					</div>
				</form>
			</div>

			<!-- Results area -->
			<admin-table
				title="AI Chat Messages"
				icon="💬"
				:columns="tableColumns"
				:data="processedMessages"
				:loading="false"
				:selectable="true"
				empty-message="No messages found."
				@row-select="onRowClicked"
			>
				<template #cell-body="{ row }">
					<span :class="{ 'highlighted-text': row.isHighlighted }">{{ row.displayBody }}</span>
					<button
						v-if="row.body && row.body.length > 500"
						class="btn-link"
						@click.stop="toggleMessageTruncation(row)"
					>
						{{ row.isTruncated ? '[more]' : '[less]' }}
					</button>
				</template>
			</admin-table>
		</div>
	</div>
</template>

<script>
import AdminServices from '@/admin/mixins/AdminServices';
import AdminTable from '@/admin/components/AdminTable.vue';

export default {
	name: 'AIChatMessagesPage',
	components: {
		AdminTable
	},
	mixins: [AdminServices],
	data() {
		const now = new Date();
		const startDate = new Date(now);
		startDate.setDate(now.getDate() - 2);

		return {
			isLoading: false,
			filters: {
				customerId: '',
				fromDate: startDate.toISOString().slice(0, 10),
				fromTime: startDate.toISOString().slice(11, 16),
				toDate: now.toISOString().slice(0, 10),
				toTime: now.toISOString().slice(11, 16),
			},
			messages: [],
			customers: [],
			employers: [],
			tableColumns: [
				{
					field: 'customerID',
					label: 'Customer ID',
					width: '120px',
					sortable: true
				},
				{
					field: 'employerName',
					label: 'Employer Name',
					width: '200px',
					sortable: true
				},
				{
					field: 'direction',
					label: 'Direction',
					width: '100px',
					sortable: false
				},
				{
					field: 'body',
					label: 'Message',
					width: '3fr',
					sortable: false
				},
				{
					field: 'createDateTime',
					label: 'Created Date Time',
					width: '180px',
					sortable: true,
					type: 'date'
				}
			]
		};
	},
	computed: {
		processedMessages() {
			return this.messages.map(msg => {
				const maskedBody = msg.body && typeof msg.body === 'string'
					? msg.body.replace(/£\d{1,3}(?:,\d{3})*(?:\.\d{2})?/g, '£***')
					: msg.body;

				const isTruncated = msg.isTruncated !== false;
				const truncatedBody = maskedBody && maskedBody.length > 500
					? `${maskedBody.slice(0, 500)}...`
					: maskedBody;

				return {
					...msg,
					displayBody: isTruncated ? truncatedBody : maskedBody,
					isTruncated,
					isHighlighted: msg.body && msg.body.startsWith("I'd like to talk about"),
				};
			});
		},
	},
	methods: {
		fetchMessages() {
			this.isLoading = true;

			const fromDateTime = new Date(`${this.filters.fromDate}T${this.filters.fromTime}`).toISOString();
			const toDateTime = new Date(`${this.filters.toDate}T${this.filters.toTime}`).toISOString();

			const filterParams = {
				customerId: this.filters.customerId,
				fromDateTime,
				toDateTime,
			};

			this._getChatMessages(filterParams, (data) => {
				this.messages = data
					.map(msg => ({
						...msg,
						isTruncated: true,
					}))
					.filter(msg => !/^\b[A-Z]+\b$/.test(msg.body.trim()));
				this.isLoading = false;
				this.mergeEmployerIntoMessages();
			});
		},
		loadEmployersAndCustomers() {
			this._getEmployers((employerData) => {
				this.employers = employerData;
				this._getCustomers((customerData) => {
					this.customers = customerData;
					this.fetchMessages();
				});
			});
		},
		mergeEmployerIntoMessages() {
			this.messages = this.messages.map(msg => {
				const customer = this.customers.find(cust => cust.id === msg.customerID);
				const employerID = customer ? customer.employerID : null;
				const employer = this.employers.find(emp => emp.id === employerID);
				if (employer && employer.name === 'Money Guided') {
					return null;
				}
				return {
					...msg,
					employerName: employer ? employer.name : 'Unknown'
				};
			}).filter(msg => msg !== null);
		},
		applyFilters() {
			const fromDateTime = new Date(`${this.filters.fromDate}T${this.filters.fromTime}`);
			const toDateTime = new Date(`${this.filters.toDate}T${this.filters.toTime}`);

			if (fromDateTime > toDateTime) {
				alert('The From datetime must be less than or equal to the To datetime.');
				return;
			}

			this.fetchMessages();
		},
		clearCustomerId() {
			this.filters.customerId = '';
			this.applyFilters();
		},
		onRowClicked(row) {
			if (row && row.customerID !== undefined) {
				this.filters.customerId = row.customerID;
				this.applyFilters();
			} else {
				console.warn('Row clicked but customerId is missing or undefined:', row);
			}
		},
		toggleMessageTruncation(message) {
			const targetMessage = this.messages.find(msg => msg.id === message.id);
			if (targetMessage) {
				targetMessage.isTruncated = !targetMessage.isTruncated;
			} else {
				console.warn('Message not found for toggling truncation:', message);
			}
		}
	},
	mounted() {
		this.api = this.$route.meta?.api || 'https://api.sysodev.link';
		this.loadEmployersAndCustomers();
	}
};
</script>

<style scoped>
.chat-messages-page {
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: column;
}

.chat-messages-content {
	width: 100%;
	max-width: 100%;
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
}

.filter-card {
	background: var(--admin-bg-secondary);
	border-radius: var(--border-radius-m);
	border: 1px solid var(--admin-border);
	padding: 1.5rem;
	box-shadow: var(--box-shadow);
	width: 100%;
	max-width: 100%;
	box-sizing: border-box;
	overflow: hidden;
	position: relative;
	margin-bottom: 1.5rem;
	transition: background-color 0.3s ease, border-color 0.3s ease;
}

.card-title {
	margin: 0 0 1rem 0;
	font-size: 1.1rem;
	font-weight: 600;
	color: var(--admin-text-primary);
	letter-spacing: 0.5px;
	transition: color 0.3s ease;
}

.loading-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	background-color: rgba(35, 42, 47, 0.95);
	z-index: 1000;
}

.spinner {
	width: 48px;
	height: 48px;
	border: 4px solid #3a4a50;
	border-top-color: #375a7f;
	border-radius: 50%;
	animation: spin 1s linear infinite;
}

@keyframes spin {
	to {
		transform: rotate(360deg);
	}
}

.loading-text {
	margin-top: 1rem;
	color: #e5e5e5;
	font-size: 1rem;
}

.filter-row {
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	gap: 1rem;
	align-items: end;
}

.filter-group {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
}

.filter-group label {
	font-size: 0.9rem;
	color: var(--admin-text-primary);
	font-weight: 500;
	transition: color 0.3s ease;
}

.filter-button-group {
	justify-content: flex-end;
}

.form-input {
	padding: 0.5rem 0.75rem;
	border: 1px solid var(--admin-border);
	border-radius: var(--border-radius-s);
	background: var(--admin-hover-bg);
	color: var(--admin-text-primary);
	font-size: 0.9rem;
	font-family: inherit;
	box-sizing: border-box;
	transition: all 0.2s ease;
}

.form-input::placeholder {
	color: var(--admin-text-secondary);
	opacity: 0.6;
}

.form-input:focus {
	outline: none;
	border-color: var(--background-color-alt6);
	box-shadow: 0 0 0 2px rgba(198, 202, 229, 0.2);
}

.input-with-clear {
	position: relative;
}

.clear-icon {
	position: absolute;
	top: 50%;
	right: 10px;
	transform: translateY(-50%);
	color: #d32f2f;
	cursor: pointer;
	font-size: 1.5rem;
	line-height: 1;
	font-weight: bold;
	transition: color 0.2s ease;
}

.clear-icon:hover {
	color: #b71c1c;
}

.btn {
	padding: 0.5rem 1.25rem;
	border: none;
	border-radius: var(--border-radius-s);
	font-size: 0.9rem;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s;
	font-family: inherit;
}

.btn-primary {
	background: var(--background-color-primary);
	color: white;
}

.btn-primary:hover {
	background: #332847;
	transform: translateY(-1px);
	box-shadow: 0 2px 8px rgba(51, 40, 71, 0.15);
}

.highlighted-text {
	color: var(--admin-text-secondary);
	opacity: 0.7;
}

.btn-link {
	background: none;
	border: none;
	color: var(--background-color-primary);
	cursor: pointer;
	font-size: 0.8rem;
	padding: 0 0.25rem;
	margin-left: 0.5rem;
	text-decoration: underline;
	transition: color 0.2s ease;
}

.btn-link:hover {
	color: #332847;
}

@media (max-width: 1024px) {
	.filter-row {
		grid-template-columns: 1fr 1fr;
	}
}

@media (max-width: 768px) {
	.filter-row {
		grid-template-columns: 1fr;
	}
}
</style>
